// src/pages/PhoneLogin.jsx
//
// KEY FIX: After verifyOtp succeeds, store token AND user in localStorage
// AND call updateUser so AuthContext state is immediately in sync.
// Previously on production navigate('/') fired before AuthContext
// knew the user was logged in, so Profile saw user===null and redirected back.

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import '../styles/PhoneLogin.css';

const PhoneLogin = () => {
  const [step, setStep]             = useState('phone');
  const [phone, setPhone]           = useState('');
  const [otp, setOtp]               = useState(['', '', '', '', '', '']);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [countdown, setCountdown]   = useState(0);
  const [userExists, setUserExists] = useState(false);

  const [manualRefCode, setManualRefCode] = useState('');
  const [refValidating, setRefValidating] = useState(false);
  const [refMessage, setRefMessage]       = useState(null);

  const { updateUser, user } = useAuth();
  const navigate             = useNavigate();
  const otpRefs              = useRef([]);
  const [searchParams]       = useSearchParams();

  // If already logged in, go home
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  // Capture ref code from URL ?ref=CODE
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode.toUpperCase());
      setManualRefCode(refCode.toUpperCase());
      setRefMessage({ valid: true, text: `Referral code "${refCode.toUpperCase()}" applied!` });
    }
  }, [searchParams]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleValidateRefCode = async () => {
    const code = manualRefCode.trim().toUpperCase();
    if (!code) { setRefMessage({ valid: false, text: 'Please enter a referral code.' }); return; }
    setRefValidating(true);
    setRefMessage(null);
    try {
      const api = (await import('../services/api')).default;
      const res = await api.get('/referrals/validate_code', { params: { code } });
      if (res.data.valid) {
        localStorage.setItem('referral_code', code);
        setRefMessage({ valid: true, text: "Valid! You'll get a coupon after signup." });
      } else {
        setRefMessage({ valid: false, text: res.data.message || 'Invalid referral code.' });
      }
    } catch {
      setRefMessage({ valid: false, text: 'Could not validate code. Try again.' });
    } finally {
      setRefValidating(false);
    }
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
    setError('');
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (phone.trim().length !== 10) { setError('Please enter a valid 10-digit mobile number'); return; }
    setLoading(true); setError('');
    try {
      const referralCode = localStorage.getItem('referral_code') || manualRefCode.trim().toUpperCase() || '';
      const res = await authAPI.sendOtp(phone, referralCode);
      setUserExists(res.data.user_exists);
      setStep('otp');
      setCountdown(30);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (i, val) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const n = [...otp]; n[i] = d; setOtp(n); setError('');
    if (d && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (p.length === 6) { setOtp(p.split('')); otpRefs.current[5]?.focus(); }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    const s = otp.join('');
    if (s.length !== 6) { setError('Please enter the complete 6-digit OTP'); return; }
    setLoading(true); setError('');
    try {
      const referralCode = localStorage.getItem('referral_code') || manualRefCode.trim().toUpperCase() || '';
      const res = await authAPI.verifyOtp(phone, s, referralCode);
      const { user, token } = res.data;

      // CRITICAL ORDER: store first, then update context, then navigate
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.removeItem('referral_code');

      // updateUser syncs AuthContext state immediately so Profile won't redirect
      updateUser(user);

      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    setLoading(true); setError('');
    try {
      await authAPI.resendOtp(phone);
      setCountdown(30); setOtp(['', '', '', '', '', '']);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) { setError(err.response?.data?.error || 'Failed to resend OTP.'); }
    finally { setLoading(false); }
  };

  const handleChangeNumber = () => {
    setStep('phone'); setOtp(['', '', '', '', '', '']);
    setError(''); setCountdown(0);
  };

  const otpComplete  = otp.join('').length === 6;
  const phoneValid   = phone.length === 10;
  const hasStoredRef = !!localStorage.getItem('referral_code') || !!manualRefCode;

  return (
    <div className="pl-page">
      <div className="pl-blob pl-blob--tl" aria-hidden="true" />
      <div className="pl-blob pl-blob--br" aria-hidden="true" />

      <div className="pl-card">
        <div className="pl-brand">
          <Link to="/" className="pl-logo-link">
            <span className="pl-logo-text">CarYugo</span>
          </Link>
        </div>

        {hasStoredRef && (
          <div className="pl-referral-banner">
            You were referred! Sign up to get a coupon.
          </div>
        )}

        {step === 'phone' ? (
          <>
            <h1 className="pl-title">Login to view Account</h1>
            <p className="pl-sub">Enter your mobile number to continue</p>

            <form onSubmit={handleSendOtp} className="pl-form">
              <div className={`pl-field-wrap${error ? ' pl-field-wrap--err' : ''}${phoneValid ? ' pl-field-wrap--ok' : ''}`}>
                <label className="pl-label">Mobile Number</label>
                <div className="pl-phone-row">
                  <span className="pl-flag-cc">IN +91</span>
                  <input
                    type="tel"
                    className="pl-phone-input"
                    placeholder="enter your mobile number"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={e => e.key === 'Enter' && phoneValid && handleSendOtp()}
                    maxLength="10"
                    disabled={loading}
                    autoFocus
                  />
                  {phoneValid && <span className="pl-tick" aria-label="Valid">✓</span>}
                </div>
                {error && <p className="pl-err">{error}</p>}
              </div>

              <p className="pl-terms">
                By logging in, I agree to the{' '}
                <Link to="/terms" className="pl-link">terms</Link>{' '}and{' '}
                <Link to="/privacy" className="pl-link">privacy policy</Link>
              </p>

              <button type="submit" className="pl-btn" disabled={loading || !phoneValid}>
                {loading ? <span className="pl-spinner" /> : 'Verify your number'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="pl-title">
              {userExists ? 'Welcome Back!' : 'Create your account'}
            </h1>
            <p className="pl-sub">
              OTP sent to <strong>+91 {phone}</strong>{' '}
              <button className="pl-change-btn" onClick={handleChangeNumber} type="button">
                Change
              </button>
            </p>

            {!userExists && (manualRefCode || localStorage.getItem('referral_code')) && (
              <div className="pl-referral-banner">
                Referral code applied — you will get a coupon after signup!
              </div>
            )}

            <form onSubmit={handleVerifyOtp} className="pl-form">
              <div className="pl-otp-row" onPaste={handleOtpPaste}>
                {otp.map((d, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    type="tel"
                    className={`pl-otp-box${d ? ' pl-otp-box--filled' : ''}`}
                    value={d}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    maxLength="1"
                    disabled={loading}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              {error && <p className="pl-err pl-err--center">{error}</p>}

              <button type="submit" className="pl-btn" disabled={loading || !otpComplete}>
                {loading ? <span className="pl-spinner" /> : (userExists ? 'Login' : 'Create Account')}
              </button>

              <div className="pl-resend">
                {countdown > 0 ? (
                  <p className="pl-countdown">Resend OTP in <strong>{countdown}s</strong></p>
                ) : (
                  <button type="button" className="pl-resend-btn" onClick={handleResendOtp} disabled={loading}>
                    Resend OTP
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PhoneLogin;